import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIChat = () => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).substring(7));
  const messagesEndRef = useRef(null);

  // Set initial greeting based on language
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: t('aiChatbotGreeting')
    }]);
  }, [lang]);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: text,
        session_id: sessionId,
        language: lang
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('aiChatbotError')
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = lang === 'es' 
    ? [
        '¿Qué documentos necesito para mis impuestos?',
        '¿Puedo deducir gastos de oficina en casa?',
        '¿Cuándo es la fecha límite para declarar?'
      ]
    : [
        'What documents do I need for my taxes?',
        'Can I deduct home office expenses?',
        'When is the tax filing deadline?'
      ];

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-105 ${open ? 'scale-0' : 'scale-100'}`}
        style={{ background: '#0B1F3A' }}
        data-testid="ai-chat-toggle"
      >
        <MessageCircle size={24} className="text-[#D4AF37]" />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          open ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'
        }`}
        style={{ maxHeight: '600px' }}
        data-testid="ai-chat-window"
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between" style={{ background: '#0B1F3A' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#D4AF3730' }}>
              <Bot size={22} style={{ color: '#D4AF37' }} />
            </div>
            <div>
              <div className="font-heading font-semibold text-white text-sm">{t('aiChatbotName')}</div>
              <div className="text-xs text-blue-300">MaTaxPreparer LLC</div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="text-blue-300 hover:text-white transition-colors" data-testid="ai-chat-close">
            <X size={20} />
          </button>
        </div>

        {/* Messages */}
        <div className="h-[320px] overflow-y-auto p-4 space-y-4 bg-[#F8FAFC]">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl p-3 ${
                msg.role === 'user'
                  ? 'bg-[#0B1F3A] text-white rounded-br-sm'
                  : 'bg-white text-[#475569] border border-gray-100 shadow-sm rounded-bl-sm'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  {msg.role === 'user'
                    ? <User size={14} className="text-blue-200" />
                    : <Bot size={14} className="text-[#0B1F3A]" />
                  }
                  <span className="text-[10px] font-semibold uppercase tracking-wider opacity-60">
                    {msg.role === 'user' ? (lang === 'es' ? 'Usted' : 'You') : 'Alex'}
                  </span>
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white rounded-2xl rounded-bl-sm p-3 border border-gray-100 shadow-sm">
                <Loader size={18} className="animate-spin text-[#0B1F3A]" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Questions */}
        {messages.length <= 1 && (
          <div className="px-4 py-3 bg-white border-t border-gray-100">
            <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2">{t('quickQuestions')}</div>
            <div className="flex flex-wrap gap-2">
              {quickQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(q); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-gray-100 bg-white">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={t('typeYourQuestion')}
              className="flex-1 px-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:border-[#2563EB] text-sm"
              data-testid="ai-chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-50"
              style={{ background: '#0B1F3A' }}
              data-testid="ai-chat-send"
            >
              <Send size={16} className="text-[#D4AF37]" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIChat;
